import { Component, CUSTOM_ELEMENTS_SCHEMA, ElementRef, inject, signal, WritableSignal } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { ReactiveFormsModule, FormBuilder, FormGroup } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { SCDService } from '../../services/scd.service';
import { EMPTY, finalize, Observable, tap, catchError, of } from 'rxjs';
import { TextValueAccessor } from '@shui/angular';
import { SCDUser } from '@shs-digi-care-monorepo/interfaces';

enum BUTTON_TAGS {
    BUTTON = 'BUTTON',
    SH_BUTTON = 'SH_BUTTON'
}

@Component({
    selector: 'sh-scd-picker-dialog',
    standalone: true,
    imports: [ReactiveFormsModule, TranslateModule, TextValueAccessor],
    templateUrl: './sh-scd-picker-dialog.component.html',
    schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class ShScdPickerDialogComponent {
    private readonly fb = inject(FormBuilder);
    private readonly scdService = inject(SCDService);
    private readonly dialogRef = inject(MatDialogRef<ShScdPickerDialogComponent>);
    private readonly elRef = inject(ElementRef);
    private readonly translateService = inject(TranslateService);

    public formGroup: FormGroup = this.fb.group({
        firstName: [''],
        lastName: [''],
        gid: ['']
    });
    public searchResults: WritableSignal<SCDUser[]> = signal([]);
    public loading: WritableSignal<boolean> = signal(false);
    public firstNameError: WritableSignal<string> = signal('');
    public lastNameError: WritableSignal<string> = signal('');

    public onSearch(): void {
        this.announce(this.translateService.instant('scd-picker.errors.search-button-clicked'));
        if (!this.searchValid()) {
            return;
        }

        this.loading.set(true);
        const { firstName, lastName, gid } = this.formGroup.value;
        let observable: Observable<SCDUser | void>;

        if (gid) {
            observable = this.scdService.getUserByGid(gid);
        } else if (firstName && lastName) {
            observable = this.scdService.getUserByFirstAndLastName(firstName, lastName);
        } else {
            observable = EMPTY;
        }

        observable
            .pipe(
                tap((result) => {
                    if (!result || Object.keys(result).length === 0) {
                        this.announce(this.translateService.instant('common.scd-picker.errors.no-results-found'));
                        this.searchResults.set([]);
                        return;
                    }
                    this.searchResults.set([result as SCDUser]);
                    this.announce(
                        this.translateService.instant('common.scd-picker.found-results', {
                            count: 1
                        })
                    );
                }),
                catchError(() => {
                    this.announce(this.translateService.instant('common.scd-picker.errors.no-results-found'));
                    this.searchResults.set([]);
                    return of([]);
                }),
                finalize(() => this.loading.set(false))
            )
            .subscribe();
    }

    public onSelect(item: SCDUser): void {
        this.dialogRef.close(item);
    }

    public onClose(): void {
        this.dialogRef.close();
    }

    public focusNext(event: KeyboardEvent): void {
        event.preventDefault();
        event.stopPropagation();
        const current = event.target as HTMLElement;
        if (current.tagName === BUTTON_TAGS.BUTTON || current.tagName === BUTTON_TAGS.SH_BUTTON) {
            current.click();
        }
        setTimeout(() => {
            const focusable = Array.from(
                this.elRef.nativeElement.querySelectorAll('input, button, sh-button, [tabindex="0"]')
            ) as HTMLElement[];
            const idx = focusable.indexOf(current.closest('input, button, sh-button, [tabindex="0"]') as HTMLElement);
            const next = focusable[idx + 1];
            if (next) next.focus();
        });
    }

    private announce(message: string): void {
        setTimeout(() => {
            const live = this.elRef.nativeElement.querySelector('#scd-announcement');
            if (live) {
                live.textContent = '';
                live.textContent = message;
            }
        });
    }

    private searchValid(): boolean {
        this.firstNameError.set('');
        this.lastNameError.set('');
        const { firstName, lastName, gid } = this.formGroup.value;

        if (gid) {
            return true;
        }
        if (firstName && !lastName) {
            this.lastNameError.set('common.scd-picker.errors.last-name-required');
            this.announce(this.translateService.instant('common.scd-picker.errors.last-name-required'));
            return false;
        }
        if (lastName && !firstName) {
            this.firstNameError.set('common.scd-picker.errors.first-name-required');
            this.announce(this.translateService.instant('common.scd-picker.errors.first-name-required'));
            return false;
        }
        return true;
    }
}
